/**
 * ブラウザで zip を開く（2026-10-04 creator/web-upload・アップロードのページ pages/UploadPage.tsx）。
 *
 * WHY 自前で書くか: 外の部品（JSZip・fflate など）を足すには承認とダウンロードが要る（指示書: npm のダウンロードをしない）。
 *   zip の読み取りに要るのは「最後の記録 → 中央の目録 → 各ファイルの頭 → 中身」をたどるだけで、圧縮の解凍はブラウザの
 *   DecompressionStream('deflate-raw')（Chrome 80+・Safari 16.4+・Firefox 113+・Node 21.2+）に任せられる = 100 行ほどで済む。
 * 対応: 無圧縮（method 0）と deflate（method 8）。チャットの AI（Python の zipfile・JS の zip）や OS の「圧縮」が作る zip はこの 2 つ。
 * 断る（ZipError.code）: not_zip（zip ではない・途中で切れている）/ encrypted（パスワードつき）/ too_big（ZIP64 = 4GB 級・ページでは扱わない）/
 *   unsupported（ほかの圧縮方式）/ broken（中身が壊れている・大きさが合わない）。
 * 中身の正しさ（CRC）は見ない: 本体が PUT のたびに sha256 と大きさを確かめる（hosting の blob の口）= 壊れた中身が公開されることは無い。
 * 依存なし（node --test を型の除去だけで回すため）。
 */

export type ZipErrorCode = 'not_zip' | 'encrypted' | 'too_big' | 'unsupported' | 'broken';

export class ZipError extends Error {
  // 引数の public（parameter properties）は型の除去（node --experimental-strip-types）で通らない = 普通に代入する
  readonly code: ZipErrorCode;
  constructor(code: ZipErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

export interface ZipFile {
  /** zip の中の名前（区切りは / のまま。整えるのは uploadFiles.ts の normalizeUpload） */
  path: string;
  bytes: Uint8Array;
}

const EOCD_SIG = 0x06054b50;
const CEN_SIG = 0x02014b50;
const LOC_SIG = 0x04034b50;

/** zip の中のファイルを全部取り出す（フォルダの行は飛ばす）。順番は中央の目録の順 */
export async function readZip(data: Uint8Array): Promise<ZipFile[]> {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  // 最後の記録（EOCD）を後ろから探す。後ろにコメント（最大 65535 バイト）が付いていることがある
  let eocd = -1;
  for (let i = data.length - 22; i >= Math.max(0, data.length - 22 - 0xffff); i--) {
    if (view.getUint32(i, true) === EOCD_SIG) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new ZipError('not_zip', 'This file is not a zip (or it was cut off while downloading).');
  const count = view.getUint16(eocd + 10, true);
  const cdSize = view.getUint32(eocd + 12, true);
  const cdOffset = view.getUint32(eocd + 16, true);
  if (count === 0xffff || cdOffset === 0xffffffff || cdSize === 0xffffffff) throw new ZipError('too_big', 'This zip is too big to open here.');
  if (cdOffset + cdSize > eocd) throw new ZipError('not_zip', 'This zip is damaged (the table of contents is missing).');

  const dec = new TextDecoder('utf-8');
  // 名前の文字: bit 11 が立っていれば UTF-8（決まり）。立っていなくても今の道具は UTF-8 で書くことが多い = UTF-8 で読む
  // （古い Windows の CP437 / Shift_JIS の名前は文字化けしうるが、ゲームのファイル名はほぼ英数字）
  const out: ZipFile[] = [];
  let p = cdOffset;
  for (let n = 0; n < count; n++) {
    if (p + 46 > data.length || view.getUint32(p, true) !== CEN_SIG) throw new ZipError('not_zip', 'This zip is damaged (bad table of contents).');
    const flags = view.getUint16(p + 8, true);
    const method = view.getUint16(p + 10, true);
    const compSize = view.getUint32(p + 20, true);
    const size = view.getUint32(p + 24, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localOffset = view.getUint32(p + 42, true);
    const name = dec.decode(data.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;
    if (name.endsWith('/')) continue; // フォルダの行
    if (flags & 0x0001) throw new ZipError('encrypted', 'This zip has a password. Make a zip without a password.');
    if (compSize === 0xffffffff || size === 0xffffffff || localOffset === 0xffffffff) throw new ZipError('too_big', 'This zip is too big to open here.');
    if (localOffset + 30 > data.length || view.getUint32(localOffset, true) !== LOC_SIG) throw new ZipError('not_zip', 'This zip is damaged (a file header is missing).');
    // 中身の開始 = ファイルの頭（30 バイト）+ 名前 + 追加の欄（中央の目録と長さが違うことがあるので、ファイルの頭の値を使う）
    const start = localOffset + 30 + view.getUint16(localOffset + 26, true) + view.getUint16(localOffset + 28, true);
    if (start + compSize > data.length) throw new ZipError('not_zip', 'This zip is damaged (cut off).');
    const raw = data.subarray(start, start + compSize);
    let bytes: Uint8Array;
    if (method === 0) bytes = raw.slice();
    else if (method === 8) bytes = await inflateRaw(raw);
    else throw new ZipError('unsupported', `"${name}" uses a kind of compression this page cannot open. Make the zip again with the usual settings.`);
    if (bytes.length !== size) throw new ZipError('broken', `"${name}" in this zip is damaged.`);
    out.push({ path: name, bytes });
  }
  return out;
}

/** deflate（zip の method 8）を解く。ブラウザの DecompressionStream に任せる */
async function inflateRaw(raw: Uint8Array): Promise<Uint8Array> {
  try {
    const stream = new Blob([raw as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    throw new ZipError('broken', 'A file in this zip is damaged.');
  }
}
